import { toCurrency } from '@buyerflo/core/helperFunctions/string'
import { useColorScheme } from 'colorScheme'
import { InfoOutlined } from 'components/icons'
import { format } from 'date-fns'
import { useEditMode } from 'purchasers/editMode'
import { omit, path } from 'ramda'
import { cloneElement, forwardRef, useEffect, useState } from 'react'
import type { GetProps } from 'tamagui'
import { Input, SizableText, TextArea, XStack, YStack } from 'tamagui'
import { Label } from './label'
import { NumericInput } from './numericInput'

type TamaguiInputProps = GetProps<typeof Input>

interface MyInputProps {
    /** Callback when input value changes. For dollar/number types, receives number | null. For others, receives string. */
    onChange?: (value: any) => void
    value?: string | number | null
    viewMode?: boolean
    editMode?: boolean
    /** Input type: 'dollar', 'number', 'date', or standard HTML input types */
    type?: 'dollar' | 'number' | 'date' | 'text' | 'email' | 'password' | string
    readOnly?: boolean
    /** Content to display at the start of the input (e.g., icons) */
    startDecorator?: React.ReactNode
    /** Content to display at the end of the input (e.g., units like '%', 'days') */
    endDecorator?: React.ReactNode
    placeholder?: string
    name?: string
    disabled?: boolean
    required?: boolean
    error?: boolean | any
    onBlur?: (e: any) => void
    onFocus?: (e: any) => void
    /** Tamagui size token (default: "$4") */
    size?: string
    /** Input height in pixels (default: 44) */
    height?: number
    /** Additional HTML input attributes */
    [key: string]: any
}

export const MyInput = forwardRef(({ onChange, value, viewMode, ...props }: MyInputProps, ref: any) => {
    const [internalVal, setInternalVal] = useState(value)
    const editModeProvided = useEditMode()
    const editMode = props.editMode ?? editModeProvided.editMode
    const { resolvedScheme } = useColorScheme()

    useEffect(() => {
        setInternalVal(value)
    }, [value])

    if (!editMode) {
        if (props.type == 'dollar') {
            return <SizableText width="100%">{toCurrency((value as any) ?? 0)}</SizableText>
        }

        const isEmpty = value === '' || value === null || value === undefined

        return (
            <SizableText width="100%">
                {value && props.startDecorator}
                {isEmpty ? '\u00A0' : String(value)}
                {value && props.endDecorator}
            </SizableText>
        )
    }

    const isDollarOrNumber = props.type == 'dollar' || props.type == 'number'
    const currentValue = props.type == 'date' ? internalVal : value

    // Simplified change handler - NumericInput handles its own change logic
    const createChangeHandler = () => {
        if (!onChange) return undefined

        // Date inputs: update internal state, handleBlur will process it
        if (props.type === 'date') {
            return (e: any) => setInternalVal(e?.target?.value ?? e)
        }

        // Standard text input: extract value and call onChange
        return (e: any) => onChange(e?.target?.value ?? e)
    }

    const handleChange = createChangeHandler()

    const handleBlur = (e: any) => {
        if (props.type == 'date' && onChange) {
            const nativeEvent = e?.nativeEvent || e
            const target = nativeEvent?.target || e?.target
            if (target?.valueAsDate) {
                onChange(format(target.valueAsDate, 'yyyy-MM-dd'))
            } else if (target?.value) {
                onChange(target.value)
            }
        }
        props.onBlur?.(e)
    }

    // Strip out MUI-specific props that don't apply to Tamagui
    const cleanProps = omit(
        [
            'slotProps',
            'sx',
            'startDecorator',
            'endDecorator',
            'editMode',
            'error',
            'type',
            'variant',
            'color',
        ],
        props,
    )

    const readOnlyStyle = props.readOnly
        ? {
              cursor: 'default' as const,
              pointerEvents: 'none' as const,
              opacity: 0.6,
          }
        : {}

    const inputType = isDollarOrNumber ? undefined : props.type

    if (isDollarOrNumber) {
        // Use NumericInput component which handles NumericFormat internally
        return (
            <NumericInput
                {...cleanProps}
                ref={ref}
                value={currentValue}
                onChange={onChange}
                onBlur={handleBlur}
                prefix={props.type === 'dollar' ? '$' : props.startDecorator}
                suffix={props.endDecorator as string}
                disabled={props.disabled}
                readOnly={props.readOnly}
                name={props.name}
                size={props.size ?? '$4'}
                height={props.height ?? 44}
                error={props.error}
            />
        )
    }

    // Consolidated render path for regular inputs (non-numeric)
    const startContent = props.startDecorator
    const endContent = props.endDecorator
    const showDecorators = startContent || endContent

    // Define input props once - single source of truth with Tamagui enhancements
    const inputProps = {
        size: (props.size ?? '$4') as any,
        height: props.height ?? 44,
        ...cleanProps,
        ref,
        value: currentValue != null ? String(currentValue) : '',
        onChange: handleChange ? (e: any) => handleChange(e) : undefined,
        onBlur: handleBlur,
        disabled: props.disabled,
        readOnly: props.readOnly,
        ...(inputType ? { type: inputType } : {}),
        ...(props.type === 'date' ? { style: { colorScheme: resolvedScheme } } : {}),
        ...readOnlyStyle,
        // Tamagui theme integration
        borderRadius: '$4',
        borderColor: props.error ? '$statusDanger' : '$borderColor',
        hoverStyle: {
            borderColor: props.error ? '$statusDanger' : '$borderColorHover',
        },
        focusStyle: {
            borderColor: props.error ? '$statusDanger' : '$borderColorFocus',
            outlineWidth: 0,
        },
        width: '100%',
    }

    const inputElement = <Input {...inputProps} />

    // Text decorators ($, %, days) keep their text baseline; element decorators
    // (icons, buttons) are flex-centered so they sit on the input's vertical center
    // instead of being baseline-aligned a few pixels high.
    const renderDecorator = (content: React.ReactNode) =>
        typeof content === 'string' || typeof content === 'number' ? (
            <SizableText color="$colorMuted">{content}</SizableText>
        ) : (
            <XStack
                alignItems="center"
                justifyContent="center"
                flexShrink={0}
                color="$colorMuted"
            >
                {content}
            </XStack>
        )

    // Wrap with decorators inside the border if needed
    if (showDecorators) {
        return (
            <XStack
                alignItems="center"
                gap="$2"
                width="100%"
                height={props.height ?? 44}
                borderWidth={1}
                borderColor={props.error ? '$statusDanger' : '$borderColor'}
                borderRadius="$4"
                backgroundColor="$backgroundStrong"
                paddingHorizontal="$3.5"
                hoverStyle={{
                    borderColor: props.error ? '$statusDanger' : '$borderColorHover',
                }}
                focusWithinStyle={{
                    borderColor: props.error ? '$statusDanger' : '$borderColorFocus',
                }}
            >
                {startContent && renderDecorator(startContent)}
                <Input
                    {...inputProps}
                    flex={1}
                    borderWidth={0}
                    borderColor="transparent"
                    backgroundColor="transparent"
                    padding={0}
                    hoverStyle={{ borderWidth: 0, borderColor: 'transparent' }}
                    focusStyle={{ borderWidth: 0, borderColor: 'transparent', outlineWidth: 0 }}
                    pressStyle={{ borderWidth: 0, borderColor: 'transparent' }}
                />
                {endContent && renderDecorator(endContent)}
            </XStack>
        )
    }

    return inputElement
})

interface InputProps {
    label?: string
    /** Helper text displayed below the input */
    helperText?: string
    /** Props passed to the YStack wrapper */
    formControlProps?: any
    /** Callback when input value changes. For dollar/number types, receives number | null. For others, receives string. */
    onChange?: (value: any) => void
    /** Error message to display */
    error?: string
    /** Input type: 'dollar', 'number', 'date', or standard HTML input types */
    type?: 'dollar' | 'number' | 'date' | 'text' | 'email' | 'password' | string
    editMode?: boolean
    value?: string | number | null
    placeholder?: string
    name?: string
    disabled?: boolean
    required?: boolean
    readOnly?: boolean
    /** Content to display at the start of the input (e.g., icons) */
    startDecorator?: React.ReactNode
    /** Content to display at the end of the input (e.g., units like '%', 'days') */
    endDecorator?: React.ReactNode
    /** Tamagui size token (default: "$4") */
    size?: string
    /** Input height in pixels (default: 44) */
    height?: number
    /** Additional HTML input attributes */
    [key: string]: any
}

export default ({ label, helperText, formControlProps, error, ...props }: InputProps) => {
    return (
        <YStack {...formControlProps}>
            {label && <Label>{label}</Label>}
            {helperText && (
                <SizableText
                    size="$2"
                    color="$colorMuted"
                    marginBottom="$1"
                >
                    {helperText}
                </SizableText>
            )}
            <MyInput
                {...props}
                error={!!error}
            />
            {error && (
                <SizableText
                    size="$2"
                    color="$statusDanger"
                    marginTop="$1"
                >
                    {error}
                </SizableText>
            )}
        </YStack>
    )
}

interface SlotWrapperProps {
    field: any
    label?: string
    helperText?: string
    formControlProps?: any
    children: any
    formState: any
}

export const SlotWrapper = ({
    field,
    label,
    helperText,
    formControlProps,
    children,
    formState,
}: SlotWrapperProps) => {
    const { errors } = formState
    const error = path(field.name.split('.'), errors) as any
    const { ref, ...fieldProps } = field

    return (
        <YStack {...formControlProps}>
            {label && <Label>{label}</Label>}
            {helperText && !error?.message && (
                <SizableText
                    size="$2"
                    color="$colorMuted"
                    marginBottom="$1"
                >
                    {helperText}
                </SizableText>
            )}
            {cloneElement(children, {
                inputRef: ref,
                ref,
                error: error,
                ...omit(['helperText', 'ref', 'fieldState'], fieldProps),
                ...children.props,
            })}
            {error?.message && (
                <XStack
                    alignItems="center"
                    gap="$1"
                    marginTop="$2"
                >
                    <InfoOutlined size={14} />
                    <SizableText
                        size="$2"
                        color="$statusDanger"
                    >
                        {error.message}
                    </SizableText>
                </XStack>
            )}
        </YStack>
    )
}

interface SlotInputProps extends Omit<InputProps, 'error'> {
    field: any
    formState: any
    formControlProps?: any
    readOnly?: boolean
}

export const SlotInput = ({ field, formState, ...props }: SlotInputProps) => (
    <SlotWrapper
        field={field}
        formState={formState}
        {...props}
    >
        <MyInput {...omit(['formControlProps'], props)} />
    </SlotWrapper>
)

export const SlotTextArea = ({
    field,
    formState,
    ...props
}: {
    field: any
    formState: any
    formControlProps?: any
    label?: string
    helperText?: string
    value?: string
    placeholder?: string
    disabled?: boolean
    name?: string
    onBlur?: (e: any) => void
    minRows?: number
    [key: string]: any
}) => {
    const { editMode } = useEditMode()

    if (!editMode) {
        return (
            <YStack>
                <Label>{props.label}</Label>
                <SizableText width="100%">{field.value}</SizableText>
            </YStack>
        )
    }

    return (
        <SlotWrapper
            field={field}
            formState={formState}
            {...props}
        >
            <TextArea
                size="$4"
                minHeight={88}
                width="100%"
                borderRadius="$4"
                borderColor={props.error ? '$statusDanger' : '$borderColor'}
                hoverStyle={{
                    borderColor: props.error ? '$statusDanger' : '$borderColorHover',
                }}
                focusStyle={{
                    borderColor: props.error ? '$statusDanger' : '$borderColorFocus',
                    outlineWidth: 0,
                }}
                {...omit(['fieldState', 'label', 'helperText', 'formControlProps'], props)}
            />
        </SlotWrapper>
    )
}
